import {
	Currency,
	roundPct,
	type WipLineItemAtomics,
	type WipLineItemDerived,
} from "@inrev/common";
import { Option } from "@inrev/core";

export type WipPortfolioTotalsLineItem = WipLineItemAtomics & WipLineItemDerived;

/** Numeric fields shared by a line item and the portfolio roll-up. */
export type WipAnalysisGridMetricSource = {
	contractTotalAmount: number | null;
	totalCostToDate: number | null;
	totalBilledToDate: number | null;
	totalEstimatedCost: number | null;
	estimatedCostToComplete: number | null;
	estimatedGrossProfit: number | null;
	grossProfitEarnedToDate: number | null;
	grossProfitToBeEarned: number | null;
	underbillings: number | null;
	overbillings: number | null;
	pureJobBorrow: number | null;
	cashFlowRemaining: number | null;
	grossProfitPercent: number | null;
	percentComplete: number | null;
};

const sumNullable = (values: ReadonlyArray<Option<number>>): number | null => {
	const present = values.filter(Option.isSome);
	if (present.length === 0) return null;
	return Currency.sum(present);
};

const portfolioGrossProfitPercent = (
	lineItems: ReadonlyArray<WipPortfolioTotalsLineItem>,
): number | null => {
	const paired = lineItems.filter(
		(item) => Option.isSome(item.estimatedGrossProfit) && Option.isSome(item.contractTotalAmount),
	);
	const estimatedGrossProfit = sumNullable(paired.map((item) => item.estimatedGrossProfit));
	const contractTotalAmount = sumNullable(paired.map((item) => item.contractTotalAmount));
	if (
		Option.isNone(estimatedGrossProfit) ||
		Option.isNone(contractTotalAmount) ||
		contractTotalAmount === 0
	) {
		return null;
	}
	return roundPct(estimatedGrossProfit / contractTotalAmount);
};

const portfolioPercentComplete = (
	lineItems: ReadonlyArray<WipPortfolioTotalsLineItem>,
): number | null => {
	const paired = lineItems.filter(
		(item) => Option.isSome(item.totalCostToDate) && Option.isSome(item.totalEstimatedCost),
	);
	const totalCostToDate = sumNullable(paired.map((item) => item.totalCostToDate));
	const totalEstimatedCost = sumNullable(paired.map((item) => item.totalEstimatedCost));
	if (
		Option.isNone(totalCostToDate) ||
		Option.isNone(totalEstimatedCost) ||
		totalEstimatedCost === 0
	) {
		return null;
	}
	return Math.min(100, roundPct((totalCostToDate / totalEstimatedCost) * 100));
};

/** Roll-up of every job on one extraction. Caller passes that extraction's rows only. */
export function deriveWipPortfolioTotals(
	lineItems: ReadonlyArray<WipPortfolioTotalsLineItem>,
): WipAnalysisGridMetricSource {
	return {
		contractTotalAmount: sumNullable(lineItems.map((item) => item.contractTotalAmount)),
		totalCostToDate: sumNullable(lineItems.map((item) => item.totalCostToDate)),
		totalBilledToDate: sumNullable(lineItems.map((item) => item.totalBilledToDate)),
		totalEstimatedCost: sumNullable(lineItems.map((item) => item.totalEstimatedCost)),
		estimatedCostToComplete: sumNullable(lineItems.map((item) => item.estimatedCostToComplete)),
		estimatedGrossProfit: sumNullable(lineItems.map((item) => item.estimatedGrossProfit)),
		grossProfitEarnedToDate: sumNullable(lineItems.map((item) => item.grossProfitEarnedToDate)),
		grossProfitToBeEarned: sumNullable(lineItems.map((item) => item.grossProfitToBeEarned)),
		underbillings: sumNullable(lineItems.map((item) => item.underbillings)),
		overbillings: sumNullable(lineItems.map((item) => item.overbillings)),
		pureJobBorrow: sumNullable(lineItems.map((item) => item.pureJobBorrow)),
		cashFlowRemaining: sumNullable(lineItems.map((item) => item.cashFlowRemaining)),
		grossProfitPercent: portfolioGrossProfitPercent(lineItems),
		percentComplete: portfolioPercentComplete(lineItems),
	};
}
